/**
 * AI ajanı ↔ sayfa UI köprüsü.
 *
 * Ajan "sayfada bul/filtrele" tipi eylemlerde veriyi chat'e getirmek yerine
 * ilgili sayfanın KENDİ filtrelerini doldurur; kullanıcı sonucu ekranda görüp
 * kayıt üzerinde kendisi işlem yapabilir.
 *
 * Çalışma şekli: executor isteği yayınlar (dispatch) ve ayrıca "bekleyen istek"
 * olarak saklar. Sayfa açıksa canlı event ile anında uygular; sayfa yeni
 * navigate edildiyse mount sırasında bekleyen isteği tüketir (navigate ile
 * dispatch arasındaki yarış bu şekilde çözülür).
 */

export const INVENTORY_FILTERS_EVENT = 'gts:applyInventoryFilters';

export interface InventoryFilterRequest {
  /** true → önce sayfadaki tüm filtreleri temizle */
  clearAll?: boolean;
  /** undefined = dokunma; '' dahil string = arama kutusuna yaz */
  searchText?: string;
  /** undefined = dokunma; null = filtreyi temizle */
  categoryId?: string | null;
  brandId?: string | null;
  warehouseId?: string | null;
  vatRate?: number | null;
  /** true = yalnızca aktif ürünler (pasifleri gizle) */
  onlyActive?: boolean;
  /** true = satış fiyatı 0 olanları gizle */
  hideZeroPrice?: boolean;

  // ✅ Gelişmiş filtreler (undefined = dokunma; null = temizle)
  /** Şube id (executor adı çözer) */
  branchId?: string | null;
  /** true = stoğu olmayan ürünleri gizle (stok > 0) */
  hideZeroStock?: boolean;
  /** all = dokunma/temizle; yes = e-ticarette açık; no = kapalı */
  ecommerce?: 'all' | 'yes' | 'no';
  /** Satış fiyatı aralığı */
  minSalePrice?: number | null;
  maxSalePrice?: number | null;
  /** Stok miktarı aralığı */
  minQuantity?: number | null;
  maxQuantity?: number | null;
  /** Oluşturma tarihi aralığı (yyyy-MM-dd) */
  createdFrom?: string | null;
  createdTo?: string | null;
  /** true = yalnızca resmi olmayan ürünler */
  onlyWithoutImage?: boolean;
}

let pendingInventoryFilters: InventoryFilterRequest | null = null;

/** Executor tarafı: filtre isteğini yayınla (sayfa kapalıysa mount'ta uygulanır). */
export const requestInventoryFilters = (req: InventoryFilterRequest): void => {
  pendingInventoryFilters = req;
  window.dispatchEvent(new CustomEvent(INVENTORY_FILTERS_EVENT, { detail: req }));
};

/** Sayfa tarafı: bekleyen isteği al ve temizle (tek seferlik). */
export const consumePendingInventoryFilters = (): InventoryFilterRequest | null => {
  const req = pendingInventoryFilters;
  pendingInventoryFilters = null;
  return req;
};

// ═══════════════════════════════════════════════════════════════════════════
// ÇEK/SENET SAYFA FİLTRE KÖPRÜSÜ
// ═══════════════════════════════════════════════════════════════════════════

export const CHECK_NOTES_FILTERS_EVENT = 'gts:applyCheckNotesFilters';

export interface CheckNotesFilterRequest {
  clearAll?: boolean;
  searchText?: string;
  /** CHECK | PROMISSORY_NOTE | all */
  type?: string;
  /** RECEIVED | GIVEN | all */
  direction?: string;
  /** PORTFOLIO | COLLECTED | … | all */
  status?: string;
  /** overdue | thisWeek | thisMonth | all */
  dueDateFilter?: string;
}

let pendingCheckNotesFilters: CheckNotesFilterRequest | null = null;

export const requestCheckNotesFilters = (req: CheckNotesFilterRequest): void => {
  pendingCheckNotesFilters = req;
  window.dispatchEvent(new CustomEvent(CHECK_NOTES_FILTERS_EVENT, { detail: req }));
};

export const consumePendingCheckNotesFilters = (): CheckNotesFilterRequest | null => {
  const req = pendingCheckNotesFilters;
  pendingCheckNotesFilters = null;
  return req;
};

// ═══════════════════════════════════════════════════════════════════════════
// İRSALİYE SAYFA FİLTRE KÖPRÜSÜ
// ═══════════════════════════════════════════════════════════════════════════

export const WAYBILL_FILTERS_EVENT = 'gts:applyWaybillFilters';

export interface WaybillFilterRequest {
  clearAll?: boolean;
  searchText?: string;
}

let pendingWaybillFilters: WaybillFilterRequest | null = null;

export const requestWaybillFilters = (req: WaybillFilterRequest): void => {
  pendingWaybillFilters = req;
  window.dispatchEvent(new CustomEvent(WAYBILL_FILTERS_EVENT, { detail: req }));
};

export const consumePendingWaybillFilters = (): WaybillFilterRequest | null => {
  const req = pendingWaybillFilters;
  pendingWaybillFilters = null;
  return req;
};

// ═══════════════════════════════════════════════════════════════════════════
// SİPARİŞ SAYFA FİLTRE KÖPRÜSÜ
// ═══════════════════════════════════════════════════════════════════════════

export const ORDER_FILTERS_EVENT = 'gts:applyOrderFilters';

export interface OrderFilterRequest {
  clearAll?: boolean;
  searchText?: string;
  /** sales | purchase | all */
  orderType?: string;
  /** draft | pending | confirmed | … | all */
  status?: string;
}

let pendingOrderFilters: OrderFilterRequest | null = null;

export const requestOrderFilters = (req: OrderFilterRequest): void => {
  pendingOrderFilters = req;
  window.dispatchEvent(new CustomEvent(ORDER_FILTERS_EVENT, { detail: req }));
};

export const consumePendingOrderFilters = (): OrderFilterRequest | null => {
  const req = pendingOrderFilters;
  pendingOrderFilters = null;
  return req;
};

// ═══════════════════════════════════════════════════════════════════════════
// TEKLİF SAYFA FİLTRE KÖPRÜSÜ
// ═══════════════════════════════════════════════════════════════════════════

export const OFFER_FILTERS_EVENT = 'gts:applyOfferFilters';

export interface OfferFilterRequest {
  clearAll?: boolean;
  searchText?: string;
  /** sales | purchase | given | received | all */
  offerType?: string;
  /** draft | pending | approved | rejected | invoiced | ordered | all */
  status?: string;
}

let pendingOfferFilters: OfferFilterRequest | null = null;

export const requestOfferFilters = (req: OfferFilterRequest): void => {
  pendingOfferFilters = req;
  window.dispatchEvent(new CustomEvent(OFFER_FILTERS_EVENT, { detail: req }));
};

export const consumePendingOfferFilters = (): OfferFilterRequest | null => {
  const req = pendingOfferFilters;
  pendingOfferFilters = null;
  return req;
};

// ═══════════════════════════════════════════════════════════════════════════
// DÖVİZ SAYFASI KÖPRÜSÜ (çevirici ön-doldurma + kur yenile)
// ═══════════════════════════════════════════════════════════════════════════

export const CURRENCY_PAGE_EVENT = 'gts:openCurrencyPage';

export interface CurrencyPageRequest {
  /** Çevirici tutarı */
  amount?: string | number;
  /** Kaynak para birimi kodu (TRY, USD…) */
  fromCurrency?: string;
  /** Hedef para birimi kodu */
  toCurrency?: string;
  /** buying | selling */
  rateType?: 'buying' | 'selling' | string;
  /** true → sayfada TCMB kurlarını yeniden çek */
  refreshRates?: boolean;
  /** true → ön-doldurma sonrası çeviriyi otomatik çalıştır */
  autoConvert?: boolean;
}

let pendingCurrencyPage: CurrencyPageRequest | null = null;

export const requestCurrencyPage = (req: CurrencyPageRequest): void => {
  pendingCurrencyPage = req;
  window.dispatchEvent(new CustomEvent(CURRENCY_PAGE_EVENT, { detail: req }));
};

export const consumePendingCurrencyPage = (): CurrencyPageRequest | null => {
  const req = pendingCurrencyPage;
  pendingCurrencyPage = null;
  return req;
};

// ═══════════════════════════════════════════════════════════════════════════
// FATURA / İRSALİYE / SİPARİŞ / TEKLİF FORMU KÖPRÜSÜ (SalesInvoice / PurchaseInvoice)
//
// Ajan belgeyi arka planda API ile OLUŞTURMAZ: formu ekranda açar, cariyi
// seçer, kalemleri ekler/düzenler; kaydetme kullanıcının onayıyla yine form
// üzerinden yapılır. Sayfa mount olduğunda imperatif bir API kaydeder
// (registerInvoiceFormApi); executor bu API'yi bekleyip (waitForInvoiceFormApi)
// çağırır ve sonucu ajana functionResponse olarak döndürür.
// ═══════════════════════════════════════════════════════════════════════════

export type InvoiceFormType =
  | 'sales'
  | 'purchase'
  | 'waybill'
  | 'orderSales'
  | 'orderPurchase'
  | 'offerSales'
  | 'offerPurchase';

/** Kalem ekleme/güncelleme girdisi (ajan parametreleriyle birebir). */
export interface InvoiceFormItemInput {
  itemName?: string;
  /** true → stokta aranmaz, stoksuz (manuel) kalem olarak eklenir */
  isManual?: boolean;
  quantity?: number;
  /** Girilen birim fiyat (priceType'a göre KDV hariç/dahil yorumlanır) */
  unitPrice?: number;
  priceType?: 'VAT_EXCLUDED' | 'VAT_INCLUDED';
  vatRate?: number;
  discountRate?: number;
  discountAmount?: number;
  unit?: string;
  warehouseId?: string;
}

/** Güncellenecek/silinecek kalemi bulma ölçütü: satır no (1 tabanlı) veya ad. */
export interface InvoiceFormItemTarget {
  line?: number;
  itemName?: string;
}

/** setFields ile ayarlanabilen form alanları (sayfa desteklemediğini bildirir). */
export interface InvoiceFormFields {
  invoiceDate?: string;
  dueDate?: string;
  invoiceNumber?: string;
  notes?: string;
  currency?: string;
  exchangeRate?: number | string;
  /** Yalnızca satış formu */
  paymentMethod?: string;
  tevkifatType?: string;
  stopajRate?: number;
  /** Yalnızca irsaliye formu — PDF'de fiyat görünsün mü */
  showPrices?: boolean;
  /** Yalnızca sipariş formu */
  deliveryDate?: string;
  processStock?: boolean;
  /** draft | pending | confirmed | shipped | delivered | canceled */
  orderStatus?: string;
  /** Yalnızca teklif formu — geçerlilik tarihi */
  validUntil?: string;
  /** draft | pending | approved | rejected */
  offerStatus?: string;
}

/** Sayfanın kaydettiği imperatif form API'si. Tüm metodlar ajana dönecek JSON üretir. */
export interface InvoiceFormApi {
  formType: InvoiceFormType;
  setAccountByName(name: string): Promise<Record<string, any>>;
  setFields(fields: InvoiceFormFields): Record<string, any>;
  addItem(input: InvoiceFormItemInput): Promise<Record<string, any>>;
  updateItem(target: InvoiceFormItemTarget, changes: InvoiceFormItemInput): Record<string, any>;
  removeItem(target: InvoiceFormItemTarget): Record<string, any>;
  getSummary(): Record<string, any>;
  save(): Promise<Record<string, any>>;
}

let invoiceFormApi: InvoiceFormApi | null = null;

/** Sayfa tarafı: mount'ta çağrılır; dönen fonksiyon unmount'ta çağrılmalı. */
export const registerInvoiceFormApi = (api: InvoiceFormApi): (() => void) => {
  invoiceFormApi = api;
  return () => {
    if (invoiceFormApi === api) invoiceFormApi = null;
  };
};

/**
 * Executor tarafı: açık fatura formunun API'sini bekler.
 * navigate → sayfa mount → register arasındaki gecikmeyi tolere eder.
 * expectedType verilirse yanlış form açıkken hata fırlatır (ör. satış istenirken alış).
 */
export const waitForInvoiceFormApi = async (
  expectedType?: InvoiceFormType,
  timeoutMs = 10000
): Promise<InvoiceFormApi> => {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const api = invoiceFormApi;
    if (api) {
      if (expectedType && api.formType !== expectedType) {
        const adOf = (t: InvoiceFormType) => {
          switch (t) {
            case 'sales': return 'satış faturası';
            case 'purchase': return 'alış faturası';
            case 'waybill': return 'irsaliye';
            case 'orderSales': return 'satış siparişi';
            case 'orderPurchase': return 'alış siparişi';
            case 'offerSales': return 'satış teklifi';
            case 'offerPurchase': return 'alış teklifi';
          }
        };
        const openHint =
          expectedType === 'waybill'
            ? 'openWaybillForm'
            : expectedType === 'orderSales' || expectedType === 'orderPurchase'
              ? 'openOrderForm'
              : expectedType === 'offerSales' || expectedType === 'offerPurchase'
                ? 'openOfferForm'
                : 'openInvoiceForm';
        throw new Error(
          `Ekranda ${adOf(api.formType)} formu açık ama ${adOf(expectedType)} bekleniyor — ` +
            `önce ${openHint} ile doğru formu aç.`
        );
      }
      return api;
    }
    await new Promise((r) => setTimeout(r, 250));
  }
  const notFound =
    expectedType === 'waybill'
      ? 'Açık bir irsaliye formu bulunamadı — önce openWaybillForm ile formu aç.'
      : expectedType === 'orderSales' || expectedType === 'orderPurchase'
        ? 'Açık bir sipariş formu bulunamadı — önce openOrderForm ile formu aç.'
        : expectedType === 'offerSales' || expectedType === 'offerPurchase'
          ? 'Açık bir teklif formu bulunamadı — önce openOfferForm ile formu aç.'
          : 'Açık bir fatura formu bulunamadı — önce openInvoiceForm ile satış/alış faturası formunu aç.';
  throw new Error(notFound);
};

// ═══════════════════════════════════════════════════════════════════════════
// SATIŞ EKRANLARI YÖNETİM SAYFASI KÖPRÜSÜ
// (QuickSalePage /hizli-satis · RestaurantPage /restoran · OrderManagementPage /siparis-yonetimi)
//
// Ajan YALNIZCA yönetici tarafında çalışır: ekran listeleme/oluşturma ve ayar
// modalını açma. Operasyonel POS ekranları kapsam dışıdır.
// Sayfa mount olduğunda imperatif API kaydeder; executor bekleyip çağırır.
// ═══════════════════════════════════════════════════════════════════════════

export type SalesScreensPageType = 'quickSale' | 'restaurant' | 'package';

/** Sayfadaki ekran listesi satırı (ajana dönen kompakt biçim). */
export interface SalesScreenInfo {
  id: string;
  name: string;
  branchName?: string | null;
}

/** Yönetim sayfasının kaydettiği imperatif API (üç sayfa da aynı sözleşme). */
export interface SalesScreensPageApi {
  /** Sayfada yüklü ekranlar. */
  listScreens(): SalesScreenInfo[];
  /** Ada göre ekranı bulup ayar modalını açar. */
  openSettings(screenName: string): Record<string, any>;
  /** Ekran listesini API'den yeniden yükler (yeni ekran sonrası). */
  refreshScreens(): Promise<void>;
}

const SCREENS_PAGE_LABELS: Record<SalesScreensPageType, { label: string; openHint: string }> = {
  quickSale: { label: 'Hızlı satış', openHint: 'openQuickSaleSettings' },
  restaurant: { label: 'Restoran', openHint: 'openRestaurantSettings' },
  package: { label: 'Paket sipariş', openHint: 'openPackageSettings' },
};

const salesScreensPageApis: Partial<Record<SalesScreensPageType, SalesScreensPageApi>> = {};

/** Sayfa tarafı: mount'ta çağrılır; dönen fonksiyon unmount'ta çağrılmalı. */
export const registerSalesScreensPageApi = (
  pageType: SalesScreensPageType,
  api: SalesScreensPageApi
): (() => void) => {
  salesScreensPageApis[pageType] = api;
  return () => {
    if (salesScreensPageApis[pageType] === api) delete salesScreensPageApis[pageType];
  };
};

/** Sayfa açık mı? (navigate gerekip gerekmediğine executor karar verir) */
export const getSalesScreensPageApi = (
  pageType: SalesScreensPageType
): SalesScreensPageApi | null => salesScreensPageApis[pageType] ?? null;

/** Executor tarafı: navigate → mount → register gecikmesini tolere ederek bekler. */
export const waitForSalesScreensPageApi = async (
  pageType: SalesScreensPageType,
  timeoutMs = 10000
): Promise<SalesScreensPageApi> => {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const api = salesScreensPageApis[pageType];
    if (api) return api;
    await new Promise((r) => setTimeout(r, 250));
  }
  const meta = SCREENS_PAGE_LABELS[pageType];
  throw new Error(
    `${meta.label} yönetim sayfası açık değil — ${meta.openHint} sayfayı otomatik açar, tekrar dene.`
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// SATIŞ EKRANI AYAR MODALI KÖPRÜSÜ (QuickSaleScreenSettingsModal)
//
// Aynı modal üç modda da kullanılır: hızlı satış (normal), restoran
// (isRestaurantMode) ve paket sipariş (isPackageMode) — sekmeler moda göre
// değişir, köprü hepsinde çalışır. Modal açıkken imperatif API kaydeder:
// sekme değiştirme, ayar okuma/yazma, kaydetme. Ajan alanları FORMDA
// değiştirir (arka planda yazmaz); kayıt saveQuickSaleSettings write
// eylemiyle (onay kartı) yapılır.
// ═══════════════════════════════════════════════════════════════════════════

/** setFields ile ayarlanabilen alanlar — bilinmeyen anahtar hata döndürülür. */
export interface QuickSaleSettingsFields {
  // Ödeme Yöntemleri sekmesi
  /** Ödeme yöntemi aç/kapa (etikete göre): { "Nakit": true, "Kredi Kartı": false } */
  paymentOptions?: Record<string, boolean>;
  // Kasiyer sekmesi
  useCashierSystem?: boolean;
  autoLockEnabled?: boolean;
  autoLockTimeoutSeconds?: number;
  // Depo/Stok sekmesi
  /** Depo adı veya id listesi (mevcut seçimin YERİNE geçer) */
  selectedWarehouses?: string[];
  preventNegativeStock?: boolean;
  // Yazdırma sekmesi
  /** 'single' | 'category' */
  printingMode?: string;
  /** Yazıcı adı (tanımlı/yerel yazıcılardan ada göre bulunur) */
  defaultReceiptPrinter?: string;
  // Ek Ayarlar sekmesi
  prepaidSystemEnabled?: boolean;
  showProductImages?: boolean;
  showCashChangeModal?: boolean;
  allowAccountCreationOnSale?: boolean;
  useKitchenScreen?: boolean;
  showScreenLayoutButton?: boolean;
  warnIfFiscalNotConnected?: boolean;
  useFiscalDeviceForCash?: boolean;
  // Yerel Servis sekmesi
  localServiceUrl?: string;
}

/** addPaymentOption girdisi — ad eşleştirmeleri TR-toleranslıdır. */
export interface QuickSalePaymentOptionInput {
  /** cash → kasa; pos → banka/POS hesabı; other → sabit liste (Multinet vb.) + kasa; openAccount → bağlantısız */
  type: 'cash' | 'pos' | 'openAccount' | 'other';
  /** cash/openAccount için opsiyonel görünen ad; other için zorunlu (MULTINET, SETCARD, ...) */
  label?: string;
  /** cash ve other tipleri için zorunlu kasa adı */
  cashboxName?: string;
  /** pos tipi için zorunlu banka/POS hesap adı ("Banka" veya "Banka / Alt Hesap") */
  bankAccountName?: string;
}

/** Modalın kaydettiği imperatif API. Tüm metodlar ajana dönecek JSON üretir. */
export interface QuickSaleSettingsApi {
  /** Modalın ait olduğu ekran adı. */
  screenName: string;
  /** Bu modda görünen sekme adları (moda göre değişir). */
  getTabs(): string[];
  /** Sekme adına göre geçiş (kısmi/aksansız eşleşme modal tarafında). */
  setTab(tabName: string): Record<string, any>;
  /** Aktif ayarların kompakt özeti. */
  getSummary(): Record<string, any>;
  /** Alanları formda değiştirir; kaydetmez. */
  setFields(fields: QuickSaleSettingsFields): Record<string, any>;
  /** Ödeme yöntemi bağlama seçeneklerini (kasa + POS hesapları) listeler; gerekirse lazy yükler. */
  listPaymentBindings(): Promise<Record<string, any>>;
  /** Yeni ödeme yöntemi ekler (formda; kaydetmez). */
  addPaymentOption(input: QuickSalePaymentOptionInput): Promise<Record<string, any>>;
  /** Ödeme yöntemini formdan kaldırır (etikete göre, TR-toleranslı). */
  removePaymentOption(label: string): Record<string, any>;
  /** Ekrana atanabilir personelleri ve mevcut atamaları listeler. */
  listAssignablePersonnel(): Record<string, any>;
  /** Ekrana atanan personel listesini değiştirir (add | remove | replace); kaydetmez. */
  setAssignedPersonnel(names: string[], mode: 'add' | 'remove' | 'replace'): Record<string, any>;
  /** Mutfak entegrasyonunu açar/kapatır; açarken QR menü bağlar (formda; kaydetmez). */
  setKitchenIntegration(enabled: boolean, qrMenuName?: string): Promise<Record<string, any>>;
  /**
   * Şirket geneli depo/stok taslaklarını listeler.
   * NOT: Taslaklar localStorage'da tutulur — yalnızca aynı tarayıcıda görünür.
   */
  listWarehouseTemplates(): Array<{ name: string; warehouseCount: number; updatedAt: string }>;
  /** Taslağı ada göre (TR-toleranslı) forma uygular; kaydetmez. */
  applyWarehouseTemplate(name: string): Record<string, any>;
  /** Formdaki mevcut depo/stok seçimini verilen adla taslak olarak kaydeder. */
  saveWarehouseTemplate(name: string): Record<string, any>;
  /** Kaydet butonunun akışını tetikler. */
  save(): Promise<Record<string, any>>;
  /** Modalı kapatır (kaydedilmemiş değişiklik bilgisi döner). */
  close(): Record<string, any>;
}

let quickSaleSettingsApi: QuickSaleSettingsApi | null = null;

/** Modal tarafı: açılışta çağrılır; dönen fonksiyon kapanış/unmount'ta çağrılmalı. */
export const registerQuickSaleSettingsApi = (api: QuickSaleSettingsApi): (() => void) => {
  quickSaleSettingsApi = api;
  return () => {
    if (quickSaleSettingsApi === api) quickSaleSettingsApi = null;
  };
};

/** Executor tarafı: modalın açılmasını bekler (openSettings → mount gecikmesi). */
export const waitForQuickSaleSettingsApi = async (
  timeoutMs = 10000
): Promise<QuickSaleSettingsApi> => {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (quickSaleSettingsApi) return quickSaleSettingsApi;
    await new Promise((r) => setTimeout(r, 250));
  }
  throw new Error(
    'Açık bir satış ekranı ayar modalı yok — önce openQuickSaleSettings / openRestaurantSettings / ' +
      'openPackageSettings ile ekranın ayarlarını aç.'
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// GÜN SONU RAPORU SAYFA KÖPRÜSÜ (/gun-sonu)
// Ajan filtreleri (tarih aralığı + şubeler) doldurup raporu çalıştırır;
// "Gün Sonu Al" işlemi bilinçli olarak ajana verilmez — kullanıcıya kalır.
// ═══════════════════════════════════════════════════════════════════════════

export const END_OF_DAY_FILTERS_EVENT = 'gts:applyEndOfDayFilters';

export interface EndOfDayFilterRequest {
  /** ISO tarih — verilmezse sayfadaki değere dokunulmaz */
  startDate?: string;
  endDate?: string;
  /** undefined = dokunma; [] = tüm şubeler; dolu dizi = seçili şubeler */
  branchIds?: string[];
  /** false verilmedikçe filtre doldurulunca rapor otomatik çalıştırılır */
  autoFilter?: boolean;
}

let pendingEndOfDayFilters: EndOfDayFilterRequest | null = null;

export const requestEndOfDayFilters = (req: EndOfDayFilterRequest): void => {
  pendingEndOfDayFilters = req;
  window.dispatchEvent(new CustomEvent(END_OF_DAY_FILTERS_EVENT, { detail: req }));
};

export const consumePendingEndOfDayFilters = (): EndOfDayFilterRequest | null => {
  const req = pendingEndOfDayFilters;
  pendingEndOfDayFilters = null;
  return req;
};

// ═══════════════════════════════════════════════════════════════════════════
// QR MENÜ SİPARİŞLERİ SAYFA FİLTRE KÖPRÜSÜ (/qr-menu/orders)
// ═══════════════════════════════════════════════════════════════════════════

export const QR_ORDERS_FILTERS_EVENT = 'gts:applyQrOrdersFilters';

export interface QrOrdersFilterRequest {
  /** undefined = dokunma; '' dahil string = arama kutusuna yaz */
  searchText?: string;
  /** all | beklemede | hazır | teslim edildi | iptal (sayfadaki select değerleri) */
  status?: string;
}

let pendingQrOrdersFilters: QrOrdersFilterRequest | null = null;

export const requestQrOrdersFilters = (req: QrOrdersFilterRequest): void => {
  pendingQrOrdersFilters = req;
  window.dispatchEvent(new CustomEvent(QR_ORDERS_FILTERS_EVENT, { detail: req }));
};

export const consumePendingQrOrdersFilters = (): QrOrdersFilterRequest | null => {
  const req = pendingQrOrdersFilters;
  pendingQrOrdersFilters = null;
  return req;
};

// ═══════════════════════════════════════════════════════════════════════════
// KURYE TAKİP EKRANI KÖPRÜSÜ (/kurye → CourierTracking)
// İki aşamalı tüketim: CourierManagement isteği GÖRÜR (peek) ve takip
// ekranını açar; CourierTracking mount olunca isteği TÜKETİP filtreleri
// uygular. peek temizlemez, consume temizler.
// ═══════════════════════════════════════════════════════════════════════════

export const COURIER_TRACKING_EVENT = 'gts:openCourierTracking';

export interface CourierTrackingRequest {
  /** Kurye adı / sipariş no / müşteri adında arar */
  searchText?: string;
  /** all | taken | delivered | cancelled */
  status?: string;
  /** all | today | yesterday | week | month */
  dateFilter?: string;
}

let pendingCourierTracking: CourierTrackingRequest | null = null;

export const requestCourierTracking = (req: CourierTrackingRequest): void => {
  pendingCourierTracking = req;
  window.dispatchEvent(new CustomEvent(COURIER_TRACKING_EVENT, { detail: req }));
};

/** CourierManagement tarafı: istek var mı diye bakar, TEMİZLEMEZ. */
export const peekPendingCourierTracking = (): CourierTrackingRequest | null =>
  pendingCourierTracking;

/** CourierTracking tarafı: isteği alır ve temizler. */
export const consumePendingCourierTracking = (): CourierTrackingRequest | null => {
  const req = pendingCourierTracking;
  pendingCourierTracking = null;
  return req;
};

// ═══════════════════════════════════════════════════════════════════════════
// RESİM KÜTÜPHANESİ SAYFA KÖPRÜSÜ (/resim-kutuphanesi)
// Klasör adı sayfa tarafında çözülür (klasör listesi yüklendikten sonra).
// ═══════════════════════════════════════════════════════════════════════════

export const IMAGE_LIBRARY_EVENT = 'gts:openImageLibrary';

export interface ImageLibraryRequest {
  /** Açılacak klasörün adı; null = ana dizine dön; undefined = dokunma */
  folderName?: string | null;
  /** all | folders | images */
  filterType?: string;
}

let pendingImageLibrary: ImageLibraryRequest | null = null;

export const requestImageLibrary = (req: ImageLibraryRequest): void => {
  pendingImageLibrary = req;
  window.dispatchEvent(new CustomEvent(IMAGE_LIBRARY_EVENT, { detail: req }));
};

export const consumePendingImageLibrary = (): ImageLibraryRequest | null => {
  const req = pendingImageLibrary;
  pendingImageLibrary = null;
  return req;
};

// ═══════════════════════════════════════════════════════════════════════════
// PERSONEL SAYFA FİLTRE KÖPRÜSÜ (/personnel)
// Şube/departman adları executor tarafında id'ye çözülür; sayfa yalnızca
// state'leri doldurur. Manager modunda şube kilidi sayfa tarafında korunur.
// ═══════════════════════════════════════════════════════════════════════════

export const PERSONNEL_FILTERS_EVENT = 'gts:applyPersonnelFilters';

export interface PersonnelFilterRequest {
  /** undefined = dokunma; '' dahil string = arama kutusuna yaz */
  searchText?: string;
  /** undefined = dokunma; null = temizle; 'unassigned' = şubesizler */
  branchId?: string | null;
  /** admin | manager | employee; null = temizle */
  role?: string | null;
  /** hasTask | available; null = temizle */
  status?: string | null;
  /** Departman id (executor adı çözer); null = temizle */
  departmentId?: string | null;
  /** true → silinen personelleri göster */
  showDeleted?: boolean;
}

let pendingPersonnelFilters: PersonnelFilterRequest | null = null;

export const requestPersonnelFilters = (req: PersonnelFilterRequest): void => {
  pendingPersonnelFilters = req;
  window.dispatchEvent(new CustomEvent(PERSONNEL_FILTERS_EVENT, { detail: req }));
};

export const consumePendingPersonnelFilters = (): PersonnelFilterRequest | null => {
  const req = pendingPersonnelFilters;
  pendingPersonnelFilters = null;
  return req;
};
